import React, { createContext, useContext, useState } from "react";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

// Initial state constants
const INITIAL_FORM_DATA = {
  customerDetails: {},
  primaryDetails: {},
  transactionItems: [],
};

const INITIAL_STATE = {
  formData: INITIAL_FORM_DATA,
  stagedAttachments: [],
  selectedTransaction: {},
  selectedPickUpTransactionItems: [],
  selectedPickUpTransaction: {},
  pickUpData: [],
  otherDetailsData: [],
};

export const createSalesFormStore = (snapshot) => createStore((set, get) => ({
  ...JSON.parse(JSON.stringify(INITIAL_STATE)),
  ...(snapshot ? JSON.parse(JSON.stringify(snapshot)) : {}),

  // Customer details
  setCustomerDetails: (details) =>
    set((state) => ({
      formData: {
        ...state.formData,
        customerDetails: { ...state.formData.customerDetails, ...details },
      },
    })),

  // Primary details
  setPrimaryDetails: (details) =>
    set((state) => ({
      formData: {
        ...state.formData,
        primaryDetails: { ...state.formData.primaryDetails, ...details },
      },
    })),

  // Transaction items
  setTransactionItems: (items) =>
    set((state) => ({
      formData: { ...state.formData, transactionItems: items },
    })),

  addStagedAttachments: (attachments) =>
    set((state) => ({
      stagedAttachments: [
        ...state.stagedAttachments,
        ...(Array.isArray(attachments) ? attachments : [attachments]),
      ],
    })),

  removeStagedAttachments: (attachmentIds) =>
    set((state) => ({
      stagedAttachments: state.stagedAttachments.filter(
        (attachment) =>
          !attachmentIds.includes(attachment.id ?? attachment.attachment_id),
      ),
    })),

  clearStagedAttachments: () => set({ stagedAttachments: [] }),

  // Pickup transaction items
  setSelectedPickUpTransactionItems: (items) =>
    set({ selectedPickUpTransactionItems: Array.isArray(items) ? [...items] : [] }),

  // Selected transaction
  setSelectedTransaction: (transaction) =>
    set({ selectedTransaction: transaction }),

  // Pickup data
  setPickUpData: (items) =>
    set({ pickUpData: items }),

  // Selected pickup transaction
  setSelectedPickUpTransaction: (tran_code, rowData) =>
    set((state) => ({
      selectedPickUpTransaction: { ...state.selectedPickUpTransaction, [tran_code]: rowData },
    })),

  // Other details
  addOtherDetails: (newDetails) =>
    set((state) => ({
      otherDetailsData: [
        ...state.otherDetailsData,
        ...(Array.isArray(newDetails) ? newDetails : [newDetails]),
      ],
    })),

  clearOtherDetails: () =>
    set({ otherDetailsData: [] }),

  // Initialize form
  initializeForm: (initialData) =>
    set((state) => ({
      formData: { ...state.formData, primaryDetails: initialData },
    })),

  // Reset functions
  clearForm: () =>
    set({ formData: JSON.parse(JSON.stringify(INITIAL_FORM_DATA)) }),

  clearTransactionState: () =>
    set({
      selectedTransaction: {},
      selectedPickUpTransactionItems: [],
      selectedPickUpTransaction: {},
    }),

  // Reset entire store
  resetStore: () => set(JSON.parse(JSON.stringify(INITIAL_STATE))),

  // Selectors (for better performance when accessing nested state)
  getCustomerDetails: () => get().formData.customerDetails,
  getPrimaryDetails: () => get().formData.primaryDetails,
  getTransactionItems: () => get().formData.transactionItems,
}));

const legacyStore = createSalesFormStore();
export const resetLegacySalesFormStore = () => legacyStore.getState().resetStore();
const SalesFormStoreContext = createContext(null);
export const SalesFormStoreProvider = ({ snapshot, children }) => {
  const [store] = useState(() => {
    const instance = createSalesFormStore(snapshot);
    instance.restoredSnapshot = snapshot ? instance.getState() : null;
    return instance;
  });
  return <SalesFormStoreContext.Provider value={store}>{children}</SalesFormStoreContext.Provider>;
};
export const useSalesFormStoreApi = () => useContext(SalesFormStoreContext) || legacyStore;
export const useSalesFormStore = (selector = (state) => state) => useStore(useSalesFormStoreApi(), selector);
// Compatibility for external legacy callers; components must use the scoped API hook.
useSalesFormStore.getState = legacyStore.getState;
export const useRestoredSalesForm = () => useSalesFormStoreApi().restoredSnapshot;
export const isInitialRestoredPickup = (snapshot, state) => Boolean(snapshot &&
  snapshot.selectedPickUpTransaction === state.selectedPickUpTransaction &&
  snapshot.selectedPickUpTransactionItems === state.selectedPickUpTransactionItems &&
  snapshot.selectedTransaction === state.selectedTransaction);
